import {Injectable} from '@angular/core';
import {ComponentFormConfig} from "../_typing/config/component-form-config";
import {moment} from "./timezone-selector.service";
import {escapeLike, julianDate, stringDate} from "../lib/utils";
import {SearchQueryOptions} from "./api/search-query-options";
import {IDMap, KeyMap, ModelID} from "../_typing/generic-types";
import {ConstantValue} from "../_models/api/generic-constant";
import {ConstantProperty} from "../_models/constant-property";
import {of, take} from "rxjs";
import {ConstantPropertyDataService} from "../data/constant-property-data.service";
import {ListResponse} from "./api/response-types";
import {Observable} from 'rxjs';
import {union as _union, difference as _difference} from "lodash-es";
import {COMPONENT_EVENTS_CONFIG} from "../_typing/config/component-events-table";
import {NotificationService} from "./notification.service";
import {FormulaPart, NameFormula} from "../_typing/config/component-name-formula-config";
import {findYearToken} from "../_typing/utils/date-format";

@Injectable({
    providedIn: 'root'
})
export class ComponentNameFormulaBuilderService {

    static readonly YEAR_WARNING_MSG =
        "This formula's custom date format has no year token (e.g. YYYY), so the per-year count can't be scoped. " +
        "Add a year token to the date format, or set the count reset frequency to daily.";

    static readonly DAY_WARNING_MSG =
        "This formula's date format has no month+day token (e.g. MM-DD), so the daily count can't be scoped to the " +
        "selected day. Add month and day tokens, or set the count reset frequency to yearly.";

    static readonly MULTIPLE_DATE_MSG = "A name formula can include at most one date part.";

    static readonly NO_SCOPE_MSG =
        "This formula's date format has no year or month+day token, so an automatic count can't be " +
        "scoped to any reset period. Add year (e.g. YYYY) or month and day (e.g. MM-DD) tokens, or use a manual count start.";

    /**
     * Normalize a (possibly unset or legacy) reset frequency: anything that isn't 'daily' is treated
     * as yearly — the default. Both the reset validation and the count-match builder route through
     * this so an unset `count_reset_frequency` always scopes the search the same way.
     */
    static normalizeResetFrequency(frequency: string): 'daily' | 'yearly' {
        return frequency === 'daily' ? 'daily' : 'yearly';
    }

    static noDateMsg(frequency: string): string {
        return `Automatic count needs a date part to scope the ${this.normalizeResetFrequency(frequency)} reset. ` +
            "Add a date to the formula, or use a manual count start.";
    }

    constructor(private propertyData: ConstantPropertyDataService,
                private notification: NotificationService) {
    }

    getFormulaString(formulaParts: FormulaPart[]): string {
        if (!formulaParts) return;
        let string = []
        formulaParts.forEach(p => {
            switch (p.type) {
                case "date":
                    string.push('[d:' + p.content + ']');
                    break;
                case "constant_property":
                    string.push('[cp:' + p.content + ']');
                    break;
                case "separator":
                    string.push(p.content);
                    break;
                case "text":
                    string.push(p.content);
                    break;
                default:
                    break
            }
        })
        return string.join('');
    }

    getNameString(formulaParts: FormulaPart[], cpDict: IDMap<ConstantProperty>): string {
        let string = [];

        formulaParts.forEach(p => {
            let value = p.value || p.content;
            let notSet = p.type === 'constant_property' && !p.value;
            if (notSet) {
                value = cpDict?.[p.content]?.attributes.name || '...';
            }
            string.push(notSet ? '[' + value + ']' : value);
        });
        return string.join('');
    }

    updateValue(formulaParts: FormulaPart[], value: ConstantValue, propertyId: ModelID): FormulaPart[] {
        if (this.hasProperty(formulaParts, propertyId)) {
            formulaParts.find(p => p.content === propertyId).value = value;
        }
        return formulaParts;
    }

    getInitialFormula(formulaParts: FormulaPart[], defaultValueDict?: KeyMap<ConstantValue>): FormulaPart[] {
        formulaParts.forEach(p => {
            switch (p.type) {
                case "date":
                    p.value = this.getDateFromFormula(p);
                    break;
                case "text":
                case "separator":
                    p.value = p.content;
                    break;
                case "constant_property":
                    p.value = defaultValueDict?.[p.content];
                    break;
                default:
                    break;
            }
        })
        return formulaParts;
    }

    updateDateValue(formulaParts: FormulaPart[], dt: Date): FormulaPart[] {
        formulaParts.forEach(p => {
            if (p.type === 'date') {
                p.value = this.getDateFromFormula(p, dt)
            }
        })
        return formulaParts;
    }

    getDateFromFormula(p: FormulaPart, dt?: Date) {
        if (p.content === 'JULIAN') {
            return julianDate(dt || new Date())
        } else {
            try {
                return moment(dt).format(p.content);
            } catch (error) {
                let args = {date_separator: ''}
                return stringDate(dt || new Date(), args);
            }
        }
    }

    private hasProperty(formulaParts: FormulaPart[], propertyId: ModelID) {
        return formulaParts.map(f => f.content).includes(propertyId);
    }

    getFormulaPropertyIds(formulaParts: FormulaPart[]) {
        return formulaParts.filter(p => p.type === 'constant_property')?.map(cp => cp.content);
    }

    getProperties(nameFormulas: KeyMap<NameFormula>): Observable<ListResponse<ConstantProperty>> {
        let ids: ModelID[];
        const cpIds = Object.values(nameFormulas).reduce((ids: ModelID[], formula: NameFormula) => {
            return ids.concat(this.getFormulaPropertyIds(formula));
        }, [])

        if (!cpIds) return of(null);
        return this.propertyData.getConstantProperties(cpIds)
            .pipe(take(1))

    }

    checkComponentNameFields(config: ComponentFormConfig | COMPONENT_EVENTS_CONFIG, colCpIds, requiredCpIds, cpDict): boolean {
        if (!config.selected_name_formula?.key) return true;
        const nameFormula = config.selected_name_formula;

        const cpIds = nameFormula?.formula?.filter(p => p.type === 'constant_property').map(p => p.content);
        const allIncluded = cpIds.every(item => colCpIds.includes(item) && requiredCpIds.includes(item));

        if (allIncluded) return true
        const missingCps = _union(_difference(cpIds, colCpIds).concat(_difference(cpIds, requiredCpIds)));

        this.notification.openError("The following constant properties are required by the component name formula but " +
            "are missing from your column selection or not set as 'required': " + missingCps.map(cp => cpDict[cp]?.attributes.name).join());
        return false;
    }


    /**
     * LIKE fragment scoping a name search to the year of `dt` for a moment.js format string.
     * The format is tokenized: the year token renders to the actual year digits, every other
     * date token becomes a `%` wildcard, and separator characters are preserved literally
     * (escaped). Preserving separators keeps the year anchored in the right position so the
     * pattern can't match a same-valued day or a different separator layout. Returns '' when
     * the format has no year token.
     *   ('YYYY-MM-DD', 2-Jun-2026) -> '2026-%-%'
     *   ('DD/MM/YYYY', 2-Jun-2026) -> '%/%/2026'
     *   ('MM-YYYY-DD', 2-Jun-2026) -> '%-2026-%'
     *   ('M/D/YY',     2-Jun-2026) -> '%/%/26'
     *   ('YYYYMMDD',   2-Jun-2026) -> '2026%'
     */
    buildMomentYearPattern(format: string, dt: Date): string {
        if (!findYearToken(format)) return '';
        const parts: string[] = [];
        let i = 0;
        while (i < format.length) {
            const ch = format[i];
            if (/[A-Za-z]/.test(ch)) {                  // a moment token (a run of one repeated letter)
                let j = i;
                while (j < format.length && format[j] === ch) j++;
                const run = format.slice(i, j);
                parts.push(/^Y+$/.test(run) ? escapeLike(moment(dt).format(run)) : '%');
                i = j;
            } else {                                    // a separator char ('_' becomes '\_')
                parts.push(escapeLike(ch));
                i++;
            }
        }
        return parts.join('').replace(/%+/g, '%');      // collapse adjacent wildcards
    }

    /**
     * Year LIKE fragment for any date part. Every supported format is a moment.js format
     * string except JULIAN (a 2-digit year followed by the day-of-year), which is handled
     * explicitly. Returns '' when no year can be determined.
     */
    buildYearFragment(format: string, dt: Date): string {
        if (format === 'JULIAN') {
            return `${escapeLike(moment(dt).format('YY'))}%`;  // YY leads, day-of-year follows
        }
        return this.buildMomentYearPattern(format, dt);
    }

    getDateParts(parts: FormulaPart[]): FormulaPart[] {
        return (parts ?? []).filter(p => p.type === 'date');
    }

    /** True when a year can be resolved from the format (a `Y` token, or JULIAN's leading year). */
    dateFormatIdentifiesYear(format: string): boolean {
        // The probe date is irrelevant — buildYearFragment returns '' iff the format has no year.
        return !!this.buildYearFragment(format, new Date());
    }

    /**
     * True when the format identifies a calendar day to day-granularity — i.e. it carries both a
     * month and a day-of-month token (or is JULIAN, which is year + day-of-year). A yearless
     * month+day format (e.g. MM-DD) qualifies: the daily count is intentionally allowed to span
     * years (a knowingly accepted trade-off).
     */
    dateFormatIdentifiesDay(format: string): boolean {
        return format === 'JULIAN' || (/M/.test(format) && /D/.test(format));
    }

    /**
     * Formula-level check that the count search can be scoped to the reset period. Returns a
     * user-facing reason string when the formula can't be scoped, or null when it's fine.
     * Manual count ignores dates entirely, so it's exempt. Automatic count + reset needs exactly
     * one date part that identifies the year (yearly) or the day (daily); a part that identifies
     * neither (e.g. a time-only format) can't scope any reset and returns NO_SCOPE_MSG. The daily
     * and yearly warnings are only returned when switching frequency would actually help.
     */
    validateFormulaForReset(parts: FormulaPart[], frequency: string, automaticCount: boolean): string | null {
        if (!parts?.length) return null;                                      // no formula → nothing to scope
        const dateParts = this.getDateParts(parts);
        if (!automaticCount) return null;                                     // manual count ignores dates entirely
        if (dateParts.length > 1) return ComponentNameFormulaBuilderService.MULTIPLE_DATE_MSG;
        if (dateParts.length === 0) return ComponentNameFormulaBuilderService.noDateMsg(frequency);
        const format = String(dateParts[0].content);
        const identifiesYear = this.dateFormatIdentifiesYear(format);
        const identifiesDay = this.dateFormatIdentifiesDay(format);
        if (!identifiesYear && !identifiesDay) {                              // time-only / un-scopable by any reset
            return ComponentNameFormulaBuilderService.NO_SCOPE_MSG;
        }
        if (ComponentNameFormulaBuilderService.normalizeResetFrequency(frequency) === 'daily') {
            return identifiesDay ? null : ComponentNameFormulaBuilderService.DAY_WARNING_MSG;
        }
        return identifiesYear ? null : ComponentNameFormulaBuilderService.YEAR_WARNING_MSG;
    }
}
